import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DrivePicker,
  DrivePickerDocsView,
} from '@googleworkspace/drive-picker-react'
import type { PickerPickedEvent } from '@googleworkspace/drive-picker-element'
import { AlertCircle, Check, Download, FolderOpen, Link2, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { fetchWithAuth } from '@/lib/api/core'
import {
  documentUrlForPathOrId,
  formatUserFacingStoragePath,
  type WorkspaceRouteInfo,
} from '@/lib/app-routes'
import { testIds } from '@/lib/test-ids'
import { toast } from 'sonner'
import { useWorkspace } from '@/providers/workspace-provider'
import { FolderPickerDialog } from '@/components/features/folder-picker-dialog'
import type { OpenTab } from '@/components/features/document-tabs'
import {
  GOOGLE_DRIVE_IMPORT_COMPLETE_EVENT,
  GOOGLE_DRIVE_OPEN_IMPORT_PICKER_EVENT,
  organizationIdForFolderPath,
  resolveDriveImportDestination,
  type GoogleDriveImportCompleteDetail,
  type GoogleDriveOpenImportPickerDetail,
  type DriveImportFile,
  importDriveFilesSequentially,
} from './gdoc-import-utils'

const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive'
const IMPORTABLE_MIME_TYPES =
  'application/vnd.google-apps.document,text/markdown,text/plain'

type FileProgressState = 'pending' | 'importing' | 'done' | 'error'

export type ImportProgressRow = DriveImportFile & {
  status: FileProgressState
  error?: string
  documentId?: string
}

type DriveImportPickerProps = {
  organizationId: string
  folderPath: string
  onClose: () => void
  onComplete?: (importedCount: number) => void
  /** Prefer Browse-style open; bare navigate('/doc/...') is often ignored by URL-sync. */
  onOpenDocument?: (documentId: string) => void
  /** Prefill paste field (command palette Drive URL). */
  initialUrl?: string
  /** Destination came from current Browse folder or open document parent. */
  fromBrowseContext?: boolean
}

/** Reads Vite env vars required to initialize the Google Drive Picker. */
function getPickerConfig(): { clientId: string; appId?: string } | null {
  const clientId = import.meta.env.VITE_GOOGLE_DRIVE_CLIENT_ID?.trim()
  if (!clientId) return null
  const appId = import.meta.env.VITE_GOOGLE_DRIVE_APP_ID?.trim()
  return { clientId, appId: appId || undefined }
}

type PickedDoc = {
  id?: string
  name?: string
  mimeType?: string
}

/** Maps a Picker picked event to the import API file payload shape. */
function mapPickedDocs(event: PickerPickedEvent): DriveImportFile[] {
  const docs = (event.detail.docs ?? []) as PickedDoc[]
  return docs
    .filter((doc) => doc.id && doc.name)
    .map((doc) => ({
      fileId: doc.id as string,
      name: doc.name as string,
      mimeType: doc.mimeType ?? 'application/vnd.google-apps.document',
    }))
}

function lastImportedDocumentId(rows: ImportProgressRow[]): string | undefined {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i]?.status === 'done' && rows[i]?.documentId) return rows[i].documentId
  }
  return undefined
}

/** Modal sheet showing per-file import status during a batch Drive import. */
export function ImportProgressPanel({
  rows,
  onDismiss,
  dismissing,
  dismissLabel = 'Done',
}: {
  rows: ImportProgressRow[]
  onDismiss: () => void
  dismissing?: boolean
  dismissLabel?: string
}) {
  const doneCount = rows.filter((row) => row.status === 'done').length
  const errorCount = rows.filter((row) => row.status === 'error').length
  const finished = rows.every((row) => row.status === 'done' || row.status === 'error')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div
        className="flex max-h-[580px] w-[480px] flex-col rounded-xl border border-border bg-card shadow-2xl"
        data-testid={testIds.driveSidebar.importProgressPanel}
      >
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-4">
          <div className="space-y-0.5">
            <h2 className="text-sm font-semibold">Importing from Google Drive</h2>
            <p className="text-xs text-muted-foreground">
              {finished
                ? `${doneCount} imported${errorCount ? `, ${errorCount} failed` : ''}`
                : `Processing ${rows.length} file${rows.length !== 1 ? 's' : ''}...`}
            </p>
          </div>
          {finished ? (
            <button
              type="button"
              onClick={onDismiss}
              className="text-muted-foreground transition-colors hover:text-foreground"
              aria-label="Dismiss import progress"
              data-testid={testIds.driveSidebar.importProgressDismiss}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <div className="flex-1 space-y-3 overflow-auto p-5">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm font-medium">
              {finished ? 'Import complete' : 'Import in progress'}
            </span>
            <span className="text-xs text-muted-foreground">
              {doneCount} / {rows.length}
            </span>
          </div>

          {rows.map((row) => (
            <div
              key={row.fileId}
              className="flex items-center gap-3"
              data-testid={testIds.driveSidebar.importProgressRow(row.fileId)}
            >
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                {row.status === 'importing' && (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
                )}
                {row.status === 'done' && <Check className="h-4 w-4 text-green-500" />}
                {row.status === 'pending' && (
                  <div className="h-3 w-3 rounded-full border-2 border-border" />
                )}
                {row.status === 'error' && (
                  <AlertCircle className="h-4 w-4 text-destructive" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">{row.name}</p>
                <p className="text-[10px] text-muted-foreground">
                  {row.status === 'pending'
                    ? 'Waiting...'
                    : row.status === 'importing'
                      ? 'Importing as markdown...'
                      : row.status === 'done'
                        ? 'Created in Maater'
                        : row.error ?? 'Failed'}
                </p>
              </div>
            </div>
          ))}
        </div>

        {finished ? (
          <div className="border-t border-border/60 px-5 py-4">
            <Button
              size="sm"
              className="w-full"
              onClick={onDismiss}
              disabled={dismissing}
              data-testid={testIds.driveSidebar.importProgressDismiss}
            >
              {dismissing ? 'Opening...' : dismissLabel}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Import entry: paste a Drive/Docs URL, or open Google Picker for multi-select.
 * Tree browse + context-menu Import stays the primary path for files you already see.
 */
export function DriveImportPicker({
  organizationId,
  folderPath,
  onClose,
  onComplete,
  onOpenDocument,
  initialUrl,
  fromBrowseContext = false,
}: DriveImportPickerProps) {
  const navigate = useNavigate()
  const { workspaces } = useWorkspace()
  const pickerConfig = getPickerConfig()
  const [phase, setPhase] = useState<'entry' | 'picker' | 'progress'>('entry')
  const [rows, setRows] = useState<ImportProgressRow[]>([])
  const [dismissing, setDismissing] = useState(false)
  const [driveUrl, setDriveUrl] = useState(initialUrl?.trim() ?? '')
  const [resolvingUrl, setResolvingUrl] = useState(false)
  const [destOrganizationId, setDestOrganizationId] = useState(organizationId)
  const [destFolderPath, setDestFolderPath] = useState(folderPath)
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  const [userOverrodeDest, setUserOverrodeDest] = useState(false)

  useEffect(() => {
    setDestOrganizationId(organizationId)
    setDestFolderPath(folderPath)
    setUserOverrodeDest(false)
  }, [organizationId, folderPath])

  useEffect(() => {
    if (initialUrl?.trim()) setDriveUrl(initialUrl.trim())
  }, [initialUrl])

  const routeWorkspaces: WorkspaceRouteInfo[] = useMemo(
    () =>
      workspaces.map((ws) => ({
        id: ws.id,
        slug: ws.slug || ws.id,
        name: ws.name,
        contentRoot: ws.contentRoot ?? '',
      })),
    [workspaces],
  )

  const destWorkspaceName =
    workspaces.find((ws) => ws.id === destOrganizationId)?.name?.trim() || 'Workspace'
  const destPathLabel = formatUserFacingStoragePath(destFolderPath, routeWorkspaces)

  const openImportedDocument = useCallback(
    (documentId: string | undefined) => {
      if (!documentId) {
        onClose()
        return
      }
      setDismissing(true)
      navigate(documentUrlForPathOrId(documentId))
      onClose()
    },
    [navigate, onClose],
  )

  const runImport = useCallback(
    async (files: DriveImportFile[]) => {
      setPhase('progress')
      setRows(files.map((file) => ({ ...file, status: 'pending' as const })))

      const results = await importDriveFilesSequentially({
        organizationId: destOrganizationId,
        folderPath: destFolderPath,
        files,
        onProgress: (fileId, status, result) => {
          setRows((prev) =>
            prev.map((row) =>
              row.fileId === fileId
                ? {
                    ...row,
                    status,
                    documentId: result?.documentId,
                    error: result?.error,
                  }
                : row,
            ),
          )
        },
      })

      const imported = results.filter((result) => result.status === 'done')
      if (imported.length > 0) {
        const documentId = imported[imported.length - 1]?.documentId
        const detail: GoogleDriveImportCompleteDetail = {
          folderPath: destFolderPath,
          organizationId: destOrganizationId,
          documentId,
        }
        window.dispatchEvent(
          new CustomEvent(GOOGLE_DRIVE_IMPORT_COMPLETE_EVENT, { detail }),
        )
        onComplete?.(imported.length)
        // Single success: open immediately (parity with row Import).
        if (imported.length === 1 && documentId) {
          openImportedDocument(documentId)
        }
      }
    },
    [destFolderPath, destOrganizationId, onComplete, openImportedDocument],
  )

  const handleImportFromUrl = useCallback(async () => {
    const raw = driveUrl.trim()
    if (!raw || resolvingUrl) return
    setResolvingUrl(true)
    try {
      const res = await fetchWithAuth('/api/google-drive/resolve-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: raw, organizationId: destOrganizationId }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(typeof body?.error === 'string' ? body.error : `HTTP ${res.status}`)
      }
      const data = (await res.json()) as {
        file?: { id: string; name: string; mimeType: string }
        existingDocument?: { id: string; title?: string }
        googleAccess?: boolean
      }
      if (data.existingDocument?.id) {
        toast.success(
          data.googleAccess === false
            ? 'Already linked in Maater (connected Google account cannot open this Drive file). Opening the Maater doc.'
            : 'Already linked in Maater. Opening the existing doc.',
        )
        navigate(documentUrlForPathOrId(data.existingDocument.id))
        onClose()
        return
      }
      if (!data.file?.id) throw new Error('File not found')
      await runImport([
        {
          fileId: data.file.id,
          name: data.file.name,
          mimeType: data.file.mimeType || 'application/vnd.google-apps.document',
        },
      ])
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not import that Drive link')
    } finally {
      setResolvingUrl(false)
    }
  }, [destOrganizationId, driveUrl, navigate, onClose, resolvingUrl, runImport])

  const handlePicked = useCallback(
    (event: PickerPickedEvent) => {
      const files = mapPickedDocs(event)
      if (files.length === 0) {
        setPhase('entry')
        return
      }
      void runImport(files)
    },
    [runImport],
  )

  const handleDismiss = useCallback(() => {
    openImportedDocument(lastImportedDocumentId(rows))
  }, [openImportedDocument, rows])

  const openGooglePicker = () => {
    if (!pickerConfig) {
      toast.error(
        'Google Drive Picker is not configured. Set VITE_GOOGLE_DRIVE_CLIENT_ID in your environment.',
      )
      return
    }
    setPhase('picker')
  }

  const dismissLabel = lastImportedDocumentId(rows)
    ? rows.filter((row) => row.status === 'done').length > 1
      ? 'Open last imported'
      : 'Open document'
    : 'Done'

  if (phase === 'progress') {
    return (
      <ImportProgressPanel
        rows={rows}
        onDismiss={handleDismiss}
        dismissing={dismissing}
        dismissLabel={dismissLabel}
      />
    )
  }

  if (phase === 'picker' && pickerConfig) {
    return (
      <div className="sr-only" aria-hidden data-testid={testIds.driveSidebar.importPicker}>
        <DrivePicker
          {...{
            'client-id': pickerConfig.clientId,
            ...(pickerConfig.appId ? { 'app-id': pickerConfig.appId } : {}),
            multiselect: true,
            scope: GOOGLE_DRIVE_SCOPE,
            title: 'Import from Google Drive',
          }}
          onPicked={handlePicked}
          onCanceled={() => setPhase('entry')}
          onOauthError={() => {
            toast.error('Google Drive Picker authentication failed')
            setPhase('entry')
          }}
        >
          <DrivePickerDocsView {...{ 'mime-types': IMPORTABLE_MIME_TYPES }} />
        </DrivePicker>
      </div>
    )
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div
          className="flex w-[440px] flex-col rounded-xl border border-border bg-card shadow-2xl"
          data-testid={testIds.driveSidebar.importPicker}
        >
          <div className="flex items-center justify-between border-b border-border/60 px-5 py-4">
            <div className="space-y-0.5">
              <h2 className="text-sm font-semibold">Import from Google Drive</h2>
              <p className="text-xs text-muted-foreground">
                Choose a Maater destination, then paste a link or pick files.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-muted-foreground transition-colors hover:text-foreground"
              aria-label="Close import"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            <div
              className="space-y-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2.5"
              data-testid={testIds.driveSidebar.importDestination}
            >
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                  <FolderOpen className="h-3.5 w-3.5 text-muted-foreground" />
                  Import to
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs"
                  onClick={() => setFolderPickerOpen(true)}
                  data-testid={testIds.driveSidebar.importDestinationChange}
                >
                  Change
                </Button>
              </div>
              <p className="truncate text-xs font-medium">{destWorkspaceName}</p>
              <p className="truncate text-[10px] text-muted-foreground" title={destPathLabel}>
                {destPathLabel}
              </p>
              {fromBrowseContext && !userOverrodeDest ? (
                <p className="text-[10px] text-muted-foreground">Current folder</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                Paste Drive or Docs URL
              </label>
              <div className="flex gap-2">
                <Input
                  value={driveUrl}
                  onChange={(e) => setDriveUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleImportFromUrl()
                  }}
                  placeholder="https://docs.google.com/document/d/..."
                  className="h-9 flex-1 text-xs"
                  data-testid={testIds.driveSidebar.driveUrlInput}
                />
                <Button
                  type="button"
                  size="sm"
                  className="h-9 shrink-0 gap-1.5 text-xs"
                  disabled={!driveUrl.trim() || resolvingUrl || !destOrganizationId}
                  onClick={() => void handleImportFromUrl()}
                  data-testid={testIds.driveSidebar.driveUrlResolve}
                >
                  {resolvingUrl ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  Import
                </Button>
              </div>
            </div>

            <div className="relative flex items-center gap-3 py-1">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <Button
              type="button"
              variant="secondary"
              className="h-9 w-full gap-1.5 text-xs"
              disabled={!destOrganizationId}
              onClick={openGooglePicker}
            >
              <Download className="h-3.5 w-3.5" />
              Choose files from Drive…
            </Button>
          </div>

          <div className="border-t border-border/60 px-5 py-3">
            <Button type="button" variant="ghost" size="sm" className="w-full text-xs" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>

      <FolderPickerDialog
        open={folderPickerOpen}
        onOpenChange={setFolderPickerOpen}
        currentPath={destFolderPath}
        mode="import"
        title="Import destination"
        description="Choose the workspace and folder for imported Drive files."
        confirmLabel="Use this folder"
        workspaces={workspaces.map((ws) => ({
          id: ws.id,
          name: ws.name,
          slug: ws.slug,
          contentRoot: ws.contentRoot ?? '',
        }))}
        onSelect={(nextFolderPath) => {
          setDestFolderPath(nextFolderPath)
          setUserOverrodeDest(true)
          const nextOrgId = organizationIdForFolderPath(nextFolderPath, workspaces)
          if (nextOrgId) setDestOrganizationId(nextOrgId)
          setFolderPickerOpen(false)
        }}
      />
    </>
  )
}

/** Always-mounted host so palette Import works when the Drive tab is not open. */
export function DriveImportPickerHost({
  fallbackOrganizationId,
  onOpenDocument,
  openTabs = [],
  activeTabId = null,
  selectedProjectId = null,
}: {
  fallbackOrganizationId?: string
  onOpenDocument?: (documentId: string) => void
  openTabs?: OpenTab[]
  activeTabId?: string | null
  selectedProjectId?: string | null
}) {
  const { workspaces } = useWorkspace()
  const [target, setTarget] = useState<{
    organizationId: string
    folderPath: string
    initialUrl?: string
    fromBrowseContext?: boolean
  } | null>(null)

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<GoogleDriveOpenImportPickerDetail>).detail
      const activeTab = openTabs.find((tab) => tab.id === activeTabId)
      const dest = resolveDriveImportDestination({
        explicitFolderPath: detail?.folderPath,
        activeTab,
        selectedProjectId,
        fallbackOrganizationId: detail?.organizationId || fallbackOrganizationId,
        workspaces,
      })
      const orgId = dest.organizationId || fallbackOrganizationId || workspaces[0]?.id
      if (!orgId) return
      setTarget({
        organizationId: orgId,
        folderPath: dest.folderPath,
        initialUrl: detail?.initialUrl,
        fromBrowseContext: dest.fromBrowseContext,
      })
    }
    window.addEventListener(GOOGLE_DRIVE_OPEN_IMPORT_PICKER_EVENT, onOpen)
    return () => window.removeEventListener(GOOGLE_DRIVE_OPEN_IMPORT_PICKER_EVENT, onOpen)
  }, [fallbackOrganizationId, workspaces, openTabs, activeTabId, selectedProjectId])

  if (!target) return null

  return (
    <DriveImportPicker
      organizationId={target.organizationId}
      folderPath={target.folderPath}
      initialUrl={target.initialUrl}
      fromBrowseContext={target.fromBrowseContext}
      onClose={() => setTarget(null)}
      onOpenDocument={onOpenDocument}
    />
  )
}

/** Button that opens the Drive import picker flow from the Drive sidebar. */
export function DriveImportPickerTrigger({
  onClick,
  disabled,
  className,
}: {
  onClick: () => void
  disabled?: boolean
  className?: string
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      className={cn('h-8 shrink-0 gap-1.5 px-2.5 text-xs', className)}
      disabled={disabled}
      onClick={onClick}
      data-testid={testIds.driveSidebar.importPickerTrigger}
    >
      <Download className="h-3.5 w-3.5" />
      Import from Drive
    </Button>
  )
}
